import { isLevelEnabled, Logging, LogLevel } from '#common/logging';
import { withStats } from '#common/stats';
import { FilespaceEpochError } from '#common/types';
import { DEFAULT_EPOCH_DATA, EpochData, EpochManagerStats, KEY_STORE_EPOCH } from '#node/types';
export const createEpochManager = ({ nodeId, proxy, storage, errorHandler }) => {
    return new EpochManagerImpl(nodeId, proxy, storage, errorHandler);
};
class EpochManagerImpl {
    nodeId;
    proxy;
    storage;
    errorHandler;
    hubConnection = 0n;
    log = Logging.getLogger('EpochManager');
    statistics = new EpochManagerStats(this.log);
    constructor(nodeId, proxy, storage, errorHandler) {
        this.nodeId = nodeId;
        this.proxy = proxy;
        this.storage = storage;
        this.errorHandler = errorHandler;
    }
    getHubConnection() {
        return this.hubConnection;
    }
    getNodeId() {
        return this.nodeId;
    }
    async handshake() {
        const startEpoch = this.getEpoch();
        this.logDebugMessage('Performing epoch handshake, startEpoch: ', startEpoch);
        this.hubConnection = await this.doConnectEpoch(startEpoch);
        this.logDebugMessage('Got epoch connection: ', this.hubConnection);
        const newEpoch = this.incrementAndSaveEpoch();
        const prevEpoch = await this.doReportEpoch(newEpoch);
        if (startEpoch !== prevEpoch) {
            throw new FilespaceEpochError();
        }
        this.acknowledge();
        this.logDebugMessage('Acknowledged epoch: ', newEpoch);
    }
    incrementIfAcknowledged() {
        if (this.getAcknowledged()) {
            this.incrementAndSaveEpoch();
        }
        return this.getEpoch();
    }
    acknowledge() {
        this.store(new EpochData(this.getEpoch(), true));
    }
    async handleRuntimeEpochError(error) {
        await this.errorHandler(error);
        throw new FilespaceEpochError(error.message);
    }
    stats() {
        return this.statistics;
    }
    logDebugMessage(message, context) {
        if (isLevelEnabled(LogLevel.DEBUG, this.log)) {
            this.log.debug({ nodeId: this.nodeId.toString() }, `${message}: ${context.toString()}`);
        }
    }
    doConnectEpoch(startEpoch) {
        return withStats(this.statistics.connectEpoch, async () => {
            return this.proxy.connectEpoch(this.nodeId, startEpoch);
        }, { startEpoch });
    }
    async doReportEpoch(newEpoch) {
        return await withStats(this.statistics.reportEpoch, async () => {
            return this.proxy.reportEpoch(this.nodeId, newEpoch, this.hubConnection);
        }, { newEpoch });
    }
    incrementAndSaveEpoch() {
        const epoch = this.getEpoch() + 1n;
        this.store(new EpochData(epoch, false));
        return epoch;
    }
    getEpoch() {
        return this.load().epoch;
    }
    getAcknowledged() {
        return this.load().acknowledged;
    }
    store(data) {
        this.storage.put(KEY_STORE_EPOCH, data);
    }
    load() {
        return this.storage.get(KEY_STORE_EPOCH) ?? DEFAULT_EPOCH_DATA;
    }
}
